import styled from '@emotion/styled';
import { alpha, Grid, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntity } from '@/redux/slices/slice_types';
import { getTitleSeasons, selectTitle, updateSelectedSeasonFilter } from '@/redux/slices/titleNavigationDrawerSlice';
import { selectTitleNavigationDrawerSlice } from '@/redux/selectors/titleNavigationDrawerSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';

type TitleItemProps = {
  title: TitleEntity;
  isSelected?: boolean;
};

const TitleItem = ({ title, isSelected = false }: TitleItemProps) => {
  const dispatch = useAppDispatch();
  const { selectedTitlesMap } = useAppSelector(selectTitleNavigationDrawerSlice);
  const { thumbnail_link, type, name, children } = title;
  const seasons = children.filter((child) => child.entity_type === 'season');
  const hasNoSeasons = seasons.length === 0;

  const handleSelectTitle = () => {
    if (isSelected || hasNoSeasons) return;
    dispatch(selectTitle(title));

    if (selectedTitlesMap?.[title.id]) {
      dispatch(updateSelectedSeasonFilter(selectedTitlesMap[title.id].children[0]));
      return;
    }

    dispatch(getTitleSeasons(title));
  };

  return (
    <TitleItemWrapper item isSelected={isSelected} isDisabled={hasNoSeasons} onClick={handleSelectTitle}>
      {thumbnail_link ? (
        <Thumbnail src={thumbnail_link} alt={name} />
      ) : (
        <ThumbnailBox>{type === 'feature' ? <PolarisIcon icon="movie_roll" height={56} /> : <PolarisIcon icon="tv" height={56} />}</ThumbnailBox>
      )}
      <TitleDetail isSelected={isSelected}>
        <TitleInfo>
          <TitleName isSelected={isSelected}>{name}</TitleName>
          <TitleCaption isSelected={isSelected}>
            {hasNoSeasons ? 'No seasons available' : `Season${seasons.length > 1 ? 's' : ''} ${seasons.length > 1 ? `1 - ${seasons.length}` : '1'}`}
          </TitleCaption>
        </TitleInfo>
        <PolarisIcon icon="right" height={20} />
      </TitleDetail>
    </TitleItemWrapper>
  );
};

export default TitleItem;

const TitleItemWrapper = styled(Grid, { shouldForwardProp: (prop) => !['isSelected', 'isDisabled'].includes(prop) })<{
  isSelected: boolean;
  isDisabled: boolean;
}>(({ theme, isSelected, isDisabled }) => ({
  display: 'flex',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  columnGap: theme.spacing(2),
  backgroundColor: isSelected ? theme.palette.primary.main : alpha(theme.palette.common.black, 0.2),
  opacity: isDisabled ? 0.5 : 1,
  transition: 'all 0.3s ease',
  flexWrap: 'nowrap',
  '&:hover':
    !isSelected && !isDisabled
      ? {
          backgroundColor: theme.palette.action.hover,
          cursor: 'pointer'
        }
      : null
}));

const Thumbnail = styled.img(() => ({
  width: 100,
  height: 56,
  backgroundColor: '#ccc',
  borderRadius: 4
}));

const ThumbnailBox = styled.div(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.default,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userDrag: false,
  overflow: 'hidden',
  minWidth: 100,
  minHeight: 56,

  svg: {
    opacity: 0.2
  }
}));

const TitleDetail = styled('div', { shouldForwardProp: (prop) => prop !== 'isSelected' })<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: theme.spacing(1),
  flexGrow: 1,

  svg: {
    color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.secondary
  }
}));

const TitleInfo = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(0.5)
}));

const TitleName = styled(Typography, { shouldForwardProp: (prop) => prop !== 'isSelected' })<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  fontWeight: isSelected ? 'bold' : 'normal',
  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary
}));

const TitleCaption = styled(Typography, { shouldForwardProp: (prop) => prop !== 'isSelected' })<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  fontSize: theme.typography.pxToRem(14),
  color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.secondary
}));
